/**
 * SidesControl - the header's TABLE popover: who the party is, who stands
 * against them, whether positions count, and the way into a fight.
 *
 * One word in the header beside the mode, for whoever directs the table.
 * It opens a small panel (`header/HeaderMenu`):
 *
 * - THE PARTY: one of the teams on the roster. Everyone else still in the
 *   fight is the opposition - Combat's danger counts them, and Stealth's
 *   opposition list is them.
 * - THE MAP: whether positions count for the rules (see PositionsSwitch).
 * - START THE FIGHT, where the mode can break into one (passed as children).
 *
 * THE DANGER'S PIN left this panel in v9.6.0: `DangerPlates` is exported
 * for the popover that hangs from the danger readout (PhasePlate) - four
 * lit plates, pressing one PINS it, and the rocker, Follow the numbers,
 * hands it back to the count.
 */
import styles from '../styles/SidesControl.module.css';
import { DANGER_LABELS, DANGER_STEPS } from '../engine/phase/danger';
import HeaderMenu from './header/HeaderMenu';
import { sideKeyIn, sidesOfTeams } from '../engine/roster/team';

/** "Broodlord (MAJORIS), Genestealers (MEDIA)", or why there is nobody to list. */
function oppositionLine(danger, partyTeam) {
  if (!partyTeam) return 'Name the party, and everyone else still standing is counted against them.';
  if (danger.opposition.length === 0) return 'Nobody is standing against them.';
  return 'Still standing against them: ' + danger.opposition.map((o) => `${o.name} (${o.tier})`).join(', ') + '.';
}

/** A side's name: its teams' titles, "Kill-team Omega + Servitors" (2026-10-23: the party is a side). */
const sideLabel = (side) => side.teams.map((t) => t.label || t.tag).join(' + ');

function PartyPicker({ teams, partyTeam, onDirect }) {
  return (
    <label className={styles.field}>
      <span className={styles.caption}>The party is</span>
      <select
        className={styles.select}
        data-testid="party-team"
        value={sideKeyIn(teams, partyTeam) ?? ''}
        onChange={(e) => onDirect({ partyTeam: e.target.value || null })}
      >
        <option value="">Not named</option>
        {sidesOfTeams(teams).map((side) => (
          <option key={side.side} value={side.side}>
            {sideLabel(side)}
          </option>
        ))}
      </select>
    </label>
  );
}

function DangerPlates({ danger, onDirect }) {
  return (
    <div className={styles.field}>
      <span className={styles.caption}>The danger</span>
      <div className={styles.steps} role="group" aria-label="Danger level">
        {DANGER_STEPS.map((step) => (
          <button
            key={step}
            type="button"
            className={styles.step}
            aria-pressed={danger.step === step}
            data-testid={`danger-step-${step}`}
            onClick={() => onDirect({ dangerPin: step })}
          >
            {DANGER_LABELS[step]}
          </button>
        ))}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={!danger.pinned}
        className={styles.follow}
        data-testid="danger-follow"
        title={`The numbers say: ${DANGER_LABELS[danger.computed]}`}
        onClick={() => onDirect({ dangerPin: danger.pinned ? null : danger.step })}
      >
        <span className={styles.lamp} aria-hidden="true" />
        Follow the numbers
      </button>
    </div>
  );
}

/**
 * POSITIONS COUNT: whether the rules read where people stand on the map.
 * Off, range and cover are set by hand and an area attack asks who it
 * caught. Each mode has its own default; the rocker is the GM's word, and
 * "Follow the mode" takes it back.
 */
function PositionsSwitch({ positions, onDirect }) {
  return (
    <div className={styles.field}>
      <span className={styles.caption}>The map</span>
      <div className={styles.steps}>
        <button
          type="button"
          role="switch"
          aria-checked={positions.count}
          className={styles.follow}
          data-testid="positions-count"
          title="Range, cover, line of sight and area attacks are read from the map"
          onClick={() => onDirect({ positions: !positions.count })}
        >
          <span className={styles.lamp} aria-hidden="true" />
          Positions count
        </button>
        {positions.overridden && (
          <button
            type="button"
            className={styles.step}
            data-testid="positions-follow"
            onClick={() => onDirect({ positions: null })}
          >
            Follow the mode
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {import('../engine/phase/danger').DangerReading} props.danger
 * @param {{tag: string, label: string}[]} props.teams - the teams on the roster
 * @param {string|null} props.partyTeam
 * @param {{count: boolean, overridden: boolean}} props.positions - whether positions count, and whether by the GM's word
 * @param {(patch: {partyTeam?: string|null, dangerPin?: number|null, positions?: boolean|null}) => void} props.onDirect
 */
/** The danger's pin, for the popover that hangs from the danger readout (PhasePlate). */
export { DangerPlates };

/**
 * The TABLE popover (v9.6.0): who the party is, who stands against them,
 * whether positions count, and - where the mode can break into a fight -
 * the way into it (`children`: the Start the fight control). It keeps the
 * `sides-*` test ids it had as the Sides panel.
 */
export default function SidesControl({ danger, teams, partyTeam, positions, onDirect, children = null }) {
  return (
    <HeaderMenu
      label="Table"
      testId="sides-toggle"
      panelTestId="sides-panel"
      ariaLabel="The table: sides, positions, the fight"
      title="Who the party is, whether positions count, starting a fight"
    >
      <PartyPicker teams={teams} partyTeam={partyTeam} onDirect={onDirect} />
      <p className={styles.line} data-testid="sides-opposition">
        {oppositionLine(danger, partyTeam)}
      </p>
      {positions && <PositionsSwitch positions={positions} onDirect={onDirect} />}
      {children}
    </HeaderMenu>
  );
}
